/**
 * The dashboard's left rail.
 *
 * Rendered by the dashboard alone. Every other page keeps the top tab bar at
 * every width, and `lib/dashboardNav` records why: a rail takes 224px from
 * the content column, and the pages that would lose it are the ones carrying
 * 33- and 18-column tables. The dashboard has no wide table, so it is the one
 * screen where the width is free.
 *
 * Links come from `navFor` through `railSections`, so every role rule —
 * staffOnly, ownerOnly, adminOnly, supervisorOnly, hideFromOwner — is the one
 * the top bar applies. Nothing here decides who sees what.
 *
 * The account controls are not here. Search, the company picker, the alert
 * bell, change-password and sign out live in DashboardTopBar, above the
 * content column — a strip that reads across the page, rather than a column
 * that collapses to 64px and would hide them.
 *
 * They were once absent altogether, on the grounds that each would have been a
 * control that does nothing. That held while there was no cross-entity search
 * and no unread count. Search now queries the candidate and client endpoints,
 * and the bell counts the `/alerts` payload; each arrived when it had
 * something behind it. The timezone picker still does not, and is still
 * absent: the timezone is a server constant, so it could only offer the value
 * it already displays. Nor is there a presence dot, because nothing in the
 * product knows whether somebody is online.
 */
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { railSections, sectionHoldingPath } from '../lib/dashboardNav';
import { titleCaseName } from '../lib/titleCase';
import { ROLE_LABELS, useAuth } from '../store/auth';
import { useImpersonation } from '../store/impersonation';

/**
 * Where the collapsed choice is kept.
 *
 * Per browser rather than per account: it is a preference about this screen
 * at this size, not a fact about the user, and storing it on the server would
 * make a laptop and a desktop argue about it.
 */
const COLLAPSE_KEY = 'ats.dashboard.rail.collapsed';

function initialCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    // Private mode, or storage disabled. A preference that cannot be read is
    // not a reason to fail to draw the navigation.
    return false;
  }
}

export default function DashboardSidebar() {
  const user = useAuth((s) => s.user);
  const { pathname } = useLocation();
  const viewing = useImpersonation((s) => s.target) !== null;
  const sections = railSections(user, { viewing });

  const [collapsed, setCollapsed] = useState(initialCollapsed);
  /**
   * Which accordion is open.
   *
   * One at a time. Several open at once is the long rail the accordions
   * exist to avoid -- a company admin's bar flattens to 28 links.
   */
  const [openSection, setOpenSection] = useState<string | null>(() =>
    sectionHoldingPath(railSections(user, { viewing }), pathname),
  );

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // See initialCollapsed: not being able to remember the choice is not a
      // reason to refuse to make it.
    }
  }, [collapsed]);

  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-gray-200 bg-white transition-[width] duration-150 ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* The brand, which is also the home link -- the same destination the
          header's wordmark carries, and for the same reason: HomeRoute
          decides where "/" lands, so this never computes a destination. */}
      <NavLink
        to="/"
        className="group flex h-14 items-center gap-2 border-b border-gray-200 px-4 text-sm font-semibold tracking-tight text-gray-900 transition-colors duration-300 ease-out hover:text-indigo-700"
        aria-label="Go to home"
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-indigo-600 text-xs font-bold text-white transition-transform duration-300 ease-out motion-safe:group-hover:scale-110">
          B
        </span>
        {!collapsed && <span className="truncate">BNB India</span>}
      </NavLink>

      {/* The user block, directly under the brand and above the first
          heading, where the reference has it. Initials rather than a
          photo: PROFILE_PHOTO exists as a document type for employees, but
          nothing renders one in the shell and inventing an avatar here would
          be a second place for it to go stale. No presence dot -- nothing in
          the product knows whether somebody is online. */}
      {user && (
        <div
          className={`flex border-b border-gray-200 px-3 py-3 ${
            collapsed ? 'flex-col items-center gap-2' : 'items-center gap-2'
          }`}
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
            {(titleCaseName(user.fullName) ?? '?').slice(0, 1)}
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">
                {titleCaseName(user.fullName)}
              </p>
              <p className="truncate text-xs text-gray-500">
                {ROLE_LABELS[user.role]}
              </p>
            </div>
          )}
          {/* Sign out moved to DashboardTopBar, where the rest of the account
              controls now are. One sign-out button on the page, not two: a
              duplicate is one somebody clicks by accident. */}
        </div>
      )}

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => {
          const open = !section.collapsible || openSection === section.label;
          return (
            <div
              key={section.label ?? 'unheaded'}
              className={`mb-2 ${
                section.label ? '' : 'mt-1 border-t border-gray-100 pt-2'
              }`}
            >
              {section.label && !collapsed && (
                section.collapsible ? (
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-black transition-colors duration-300 ease-out hover:bg-gray-50"
                    onClick={() =>
                      setOpenSection(open ? null : section.label)
                    }
                    aria-expanded={open}
                  >
                    <span>{section.label}</span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-300 ease-out ${
                        open ? '' : '-rotate-90'
                      }`}
                    />
                  </button>
                ) : (
                  <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-black">
                    {section.label}
                  </p>
                )
              )}

              {/* Collapsed, every section is flattened: a heading with no
                  room for its text, above items with no room for theirs, is
                  two things not being shown rather than one. */}
              {(open || collapsed) && (
                <ul className="space-y-0.5">
                  {section.items.map(({ to, label, icon: Icon, end }) => (
                    <li key={to}>
                      <NavLink
                        to={to}
                        end={end}
                        title={collapsed ? label : undefined}
                        /* transition-colors, not `transition`. The shorthand
                           animates transforms too, and a moving row is what
                           this used to do wrong. */
                        className={({ isActive }) =>
                          `group relative flex items-center gap-2.5 rounded-lg
                           px-2 py-2 text-sm transition-colors duration-300
                           ease-out ${collapsed ? 'justify-center' : ''} ${
                            isActive
                              ? 'bg-indigo-50 font-medium text-indigo-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`
                        }
                      >
                        {/* The hover mark, in place of the 2px nudge that was
                            here before.

                            That nudge shifted the row out from under the
                            pointer. Anyone whose cursor sat near the left edge
                            lost the hover the instant it took effect, which
                            returned the row, which restored the hover -- a
                            loop that reads as a shake. The row cannot move,
                            so nothing here does: this is positioned out of
                            flow and takes no pointer events, so it changes
                            neither the layout nor the hit area it sits in. */}
                        {!collapsed && (
                          <span
                            aria-hidden
                            className="pointer-events-none absolute inset-y-2 left-0
                                       w-0.5 rounded-full bg-indigo-500 opacity-0
                                       transition-opacity duration-300 ease-out
                                       group-hover:opacity-100"
                          />
                        )}
                        <Icon className="h-4 w-4 shrink-0" />
                        {!collapsed && <span className="truncate">{label}</span>}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      <button
        type="button"
        className="flex items-center gap-2 border-t border-gray-200 px-4 py-2.5 text-xs text-gray-500 transition-colors duration-300 ease-out hover:bg-gray-50 hover:text-gray-700"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? (
          <PanelLeftOpen className="h-4 w-4" />
        ) : (
          <>
            <PanelLeftClose className="h-4 w-4" />
            <span>Collapse</span>
          </>
        )}
      </button>

      {/* The company picker moved to DashboardTopBar, which is where a
          control that reads across the whole page belongs -- and where it
          fits without being hidden when the rail collapses, which it was
          here. It was also dead code at the foot of this rail: the owner is
          the only account that has it, and owners were redirected off the
          dashboard. They now land on /admin/home, so it is reachable for the
          first time. */}
    </aside>
  );
}
